import { createContext, useContext, useEffect } from "react";
import { ExternalLink, X } from "lucide-react";
import type { LandDataLevel, LandDataSource } from "@/lib/api";
import { EvidenceShot, LevelBadge, AssetImage } from "./shared";

/** Everything the side panel needs to show why a single value is what it is. */
export interface EvidenceItem {
  title: string;
  value: string;
  unit?: string;
  level?: LandDataLevel | null;
  /** 口径: year, metric definition, substitutions (营收代产值, 省级近似…) */
  basis?: string[];
  source?: LandDataSource | null;
  /** extra links (e.g. road references) */
  links?: Array<{ title: string; url: string; domain?: string; fact?: string; official?: boolean }>;
  /** workspace asset (served via token) */
  asset?: { convId: string; name: string } | null;
  /** plain text shown above the sources, e.g. the fact sentence */
  quote?: string;
  /** derived value: formula and its inputs */
  formula?: string;
}

interface EvidenceCtx {
  open: (item: EvidenceItem) => void;
  searchedAt: string;
}
export const EvidenceContext = createContext<EvidenceCtx>({ open: () => {}, searchedAt: "" });
export const useEvidence = (): EvidenceCtx => useContext(EvidenceContext);

export function EvidenceDrawer({
  item,
  searchedAt,
  onClose,
}: {
  item: EvidenceItem | null;
  searchedAt: string;
  onClose: () => void;
}): JSX.Element | null {
  useEffect(() => {
    if (!item) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [item, onClose]);

  if (!item) return null;
  const src = item.source;
  const basisLine = [
    item.formula,
    ...(item.basis ?? []),
    src?.period,
    src?.publisher && src.publisher !== src.label ? src.publisher : "",
    src?.note,
  ]
    .filter(Boolean)
    .join("；");
  return (
    <div className="fixed inset-0 z-[110]" role="dialog" aria-modal="true" aria-label={`${item.title} 证据`}>
      <div className="absolute inset-0 bg-black/20" onClick={onClose} />
      <aside className="absolute right-0 top-0 flex h-full w-[440px] max-w-full flex-col bg-white shadow-2xl">
        <header className="flex items-start gap-3 border-b border-cardline bg-cardhead px-5 py-4">
          <div className="min-w-0 flex-1">
            <div className="text-[13px] text-zinc-500">{item.title}</div>
            <div className="mt-1 flex flex-wrap items-baseline gap-2">
              <span className="text-[24px] font-semibold tabular-nums text-zinc-900">{item.value}</span>
              {item.unit ? <span className="text-[13px] text-zinc-500">{item.unit}</span> : null}
              <LevelBadge level={item.level} />
            </div>
            {item.level?.note ? <div className="mt-1 text-[12px] text-zinc-500">{item.level.note}</div> : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1.5 text-zinc-500 hover:bg-white"
            aria-label="关闭"
          >
            <X className="h-4 w-4" />
          </button>
        </header>
        <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
          {src?.screenshot ? (
            <EvidenceShot url={src.screenshot} alt={`${item.title} 证据截图`} />
          ) : null}
          {item.asset ? (
            <AssetImage
              convId={item.asset.convId}
              asset={item.asset.name}
              alt={`${item.title} 证据截图`}
              className="h-64 w-full border border-cardline"
            />
          ) : null}
          {src?.url ? (
            <a
              href={src.url}
              target="_blank"
              rel="noreferrer noopener"
              className="flex items-center gap-2 rounded-lg border border-cardline px-3 py-2 text-[13px] text-brand-700 hover:bg-brand-50"
            >
              <ExternalLink className="h-4 w-4 shrink-0" />
              <span className="min-w-0 flex-1">
                <span className="block truncate">{src.label || src.domain || src.url}</span>
                <span className="block truncate text-[11px] text-zinc-400">{src.domain || src.url}</span>
              </span>
            </a>
          ) : src?.label ? (
            <div className="rounded-lg border border-cardline px-3 py-2 text-[13px] text-zinc-700">{src.label}</div>
          ) : null}
          {item.quote || src?.snippet ? (
            <blockquote className="border-l-2 border-brand-200 pl-3 text-[13px] leading-6 text-zinc-700">
              {item.quote || src?.snippet}
            </blockquote>
          ) : null}
          {basisLine ? (
            <p className="text-[12px] leading-5 text-zinc-500">
              <span className="text-zinc-400">口径：</span>
              {basisLine}
            </p>
          ) : null}
          {item.links?.length ? (
            <div>
              <div className="mb-1.5 text-[12px] text-zinc-400">全部来源（{item.links.length}）</div>
              <ul className="space-y-2">
                {item.links.map((l, i) => (
                  <li key={`${l.url}-${i}`} className="rounded-md border border-cardline px-3 py-2 text-[12px]">
                    <div className="flex items-center gap-1.5">
                      {l.official ? (
                        <span className="rounded bg-zinc-100 px-1 text-[10px] text-zinc-600">官方</span>
                      ) : null}
                      {l.url ? (
                        <a
                          href={l.url}
                          target="_blank"
                          rel="noreferrer noopener"
                          className="min-w-0 truncate text-brand-700 hover:underline"
                        >
                          {l.title || l.domain || l.url}
                        </a>
                      ) : (
                        <span className="min-w-0 truncate text-zinc-700">{l.title}</span>
                      )}
                      {l.domain ? <span className="shrink-0 text-zinc-400">· {l.domain}</span> : null}
                    </div>
                    {l.fact ? <p className="mt-1 leading-5 text-zinc-600">{l.fact}</p> : null}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {!src && !item.asset && !item.links?.length && !item.formula ? (
            <p className="text-[13px] text-zinc-400">此项没有附带来源信息。</p>
          ) : null}
          {searchedAt ? <p className="pt-2 text-[11px] text-zinc-300">搜索于 {searchedAt}</p> : null}
        </div>
      </aside>
    </div>
  );
}
